'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

// Floating particle — a single glowing dot
function Particle({ x, y, size, color, delay }: { x: string; y: string; size: number; color: string; delay: number }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        filter: `blur(${size * 0.4}px)`,
        animation: `particleFloat 6s ease-in-out infinite ${delay}s`,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
}

export default function HubToLetterBridge() {
  const bridgeRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = bridgeRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.05 }
    );
    observer.observe(el);

    function onScroll() {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const windowH = window.innerHeight;
      const progress = Math.max(0, Math.min(1, (windowH - rect.top) / (windowH + rect.height)));
      setScrollProgress(progress);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const starOpacity = Math.min(1, scrollProgress * 2.5);

  return (
    <div
      ref={bridgeRef}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(
          to bottom,
          #1C2B25 0%,
          #111e18 18%,
          #0c1610 35%,
          #080f0d 55%,
          #060d0f 72%,
          #060d12 100%
        )`,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <style>{`
        @keyframes drawStroke { to { stroke-dashoffset: 0; } }
        @keyframes particleFloat {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
          50% { transform: translateY(-18px) scale(1.15); opacity: 0.9; }
        }
        @keyframes bridgePulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.04); }
        }
        @keyframes bridgeArrow {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(10px); opacity: 1; }
        }
        @keyframes bridgeGlowPulse {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.35; }
        }
        @keyframes ol-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes ctaGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(201,168,76,0); }
          50% { box-shadow: 0 0 32px 4px rgba(201,168,76,0.18); }
        }
        .bridge-reveal {
          opacity: 0; transform: translateY(20px);
          transition: opacity 0.9s ease, transform 0.9s ease;
        }
        .bridge-reveal.visible { opacity: 1; transform: translateY(0); }
        .bridge-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 18px 40px;
          background: rgba(201,168,76,0.08);
          border: 1px solid rgba(201,168,76,0.35);
          color: #c9a84c;
          font-family: monospace;
          font-size: 11px;
          letter-spacing: .2em;
          text-transform: uppercase;
          text-decoration: none;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.3s ease, border-color 0.3s ease, transform 0.2s ease;
          animation: ctaGlow 4s ease-in-out infinite;
        }
        .bridge-cta-btn:hover {
          background: rgba(201,168,76,0.15);
          border-color: rgba(201,168,76,0.6);
          transform: translateY(-2px);
        }
        @media (max-width: 560px) {
          .bridge-pillars { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Deep radial glow */}
      <div
        style={{
          position: 'absolute',
          bottom: '-10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '80vw',
          height: '60vh',
          background: 'radial-gradient(ellipse at 50% 100%, rgba(6,20,10,0.9) 0%, rgba(20,60,20,0.25) 40%, transparent 70%)',
          pointerEvents: 'none',
          animation: 'bridgeGlowPulse 5s ease-in-out infinite',
        }}
        aria-hidden="true"
      />

      {/* Gold atmospheric haze */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '35%',
          background: 'linear-gradient(to bottom, rgba(139,105,20,0.08) 0%, transparent 100%)',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />

      {/* Particles */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: starOpacity }}>
        <Particle x="8%" y="15%" size={3} color="rgba(201,168,76,0.6)" delay={0} />
        <Particle x="18%" y="42%" size={2} color="rgba(45,122,71,0.5)" delay={0.8} />
        <Particle x="28%" y="72%" size={4} color="rgba(201,168,76,0.4)" delay={1.6} />
        <Particle x="42%" y="22%" size={2} color="rgba(45,138,135,0.5)" delay={0.4} />
        <Particle x="55%" y="65%" size={3} color="rgba(201,168,76,0.5)" delay={2.1} />
        <Particle x="68%" y="30%" size={2} color="rgba(45,122,71,0.4)" delay={1.2} />
        <Particle x="78%" y="55%" size={4} color="rgba(201,168,76,0.35)" delay={0.6} />
        <Particle x="88%" y="18%" size={2} color="rgba(138,138,184,0.4)" delay={1.9} />
        <Particle x="92%" y="78%" size={3} color="rgba(45,122,71,0.5)" delay={0.3} />
        <Particle x="35%" y="88%" size={2} color="rgba(201,168,76,0.4)" delay={2.5} />
        <Particle x="62%" y="10%" size={3} color="rgba(45,138,135,0.4)" delay={1.4} />
        <Particle x="15%" y="85%" size={2} color="rgba(201,168,76,0.3)" delay={0.9} />
      </div>

      {/* Subtle grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
          maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />

      {/* ── CONTENT ─────────────────────────────────────────────────── */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 760,
          margin: '0 auto',
          padding: 'clamp(80px, 12vw, 140px) clamp(20px, 5vw, 48px)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
        }}
      >
        {/* Uboor marker */}
        <div
          className={`bridge-reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0s', marginBottom: 32 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 18px',
              background: 'rgba(45,122,71,0.08)',
              border: '1px solid rgba(45,122,71,0.2)',
              borderRadius: 4,
            }}
          >
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 8,
                color: '#2d7a47',
                letterSpacing: '.22em',
                textTransform: 'uppercase',
              }}
            >
              Uboor · عُبُور
            </span>
            <span style={{ width: 1, height: 10, background: 'rgba(45,122,71,0.3)' }} />
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 8,
                color: 'rgba(201,168,76,0.6)',
                letterSpacing: '.18em',
                textTransform: 'uppercase',
              }}
            >
              The Crossing
            </span>
          </div>
        </div>

        {/* Central Arabic */}
        <div
          className={`bridge-reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.15s', marginBottom: 20 }}
        >
          <div
            style={{
              fontFamily: "'Amiri', 'Georgia', serif",
              fontSize: 'clamp(32px, 6vw, 56px)',
              color: 'rgba(201,168,76,0.75)',
              lineHeight: 1.3,
              direction: 'rtl',
              animation: visible ? 'bridgePulse 6s ease-in-out infinite' : 'none',
            }}
          >
            وَمَا الْحَيَاةُ الدُّنْيَا إِلَّا مَتَاعُ الْغُرُورِ
          </div>
        </div>

        {/* Ayah reference */}
        <div
          className={`bridge-reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.25s', marginBottom: 40 }}
        >
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 9,
              color: 'rgba(201,168,76,0.35)',
              letterSpacing: '.18em',
              textTransform: 'uppercase',
            }}
          >
            Surah Al-Hadid · 57:20
          </span>
        </div>

        {/* Divider */}
        <div
          className={`bridge-reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.3s', marginBottom: 48, width: '100%' }}
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.25))' }} />
            <span style={{ fontSize: 16, color: 'rgba(201,168,76,0.4)', animation: 'bridgePulse 4s ease-in-out infinite' }}>✦</span>
            <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left, transparent, rgba(201,168,76,0.25))' }} />
          </div>
        </div>

        {/* Bridge headline */}
        <div
          className={`bridge-reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.4s', marginBottom: 20 }}
        >
          <h2
            style={{
              fontFamily: "'Georgia', serif",
              fontSize: 'clamp(1.5rem, 4vw, 2.8rem)',
              color: '#F5F0E8',
              lineHeight: 1.2,
              fontWeight: 400,
              letterSpacing: '-0.01em',
            }}
          >
            The platform is the{' '}
            <span
              style={{
                fontStyle: 'italic',
                background: 'linear-gradient(90deg, #c9a84c, #e0c878, #c9a84c)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'ol-shimmer 4s linear infinite',
              }}
            >
              letter made operational.
            </span>
          </h2>
        </div>

        {/* Bridge body */}
        <div
          className={`bridge-reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.5s', marginBottom: 48 }}
        >
          <p
            style={{
              fontFamily: "'Georgia', serif",
              fontSize: 'clamp(0.9rem, 2vw, 1.05rem)',
              color: 'rgba(245,240,232,0.5)',
              lineHeight: 1.85,
              maxWidth: 560,
              margin: '0 auto',
            }}
          >
            Every tool you have encountered — the IRL Lens, the Names, the Resource Guide, the Phases — 
            flows from a single foundational document. Written to imprisoned caretakers. 
            Addressed to the whole earth.
          </p>
        </div>

        {/* Three pillars */}
        <div
          className={`bridge-reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.6s', marginBottom: 56, width: '100%' }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'clamp(8px, 2vw, 16px)',
              maxWidth: 600,
              margin: '0 auto',
            }}
            className="bridge-pillars"
          >
            {[
              { ar: 'إِصْلَاح', en: 'Islah', sub: 'Repair & Restoration', color: '#2d7a47' },
              { ar: 'خِدْمَة', en: 'Khidmah', sub: 'Service & Caretaking', color: '#c9a84c' },
              { ar: 'أَمَانَة', en: 'Amanah', sub: 'Sacred Trust', color: '#2d8a87' },
            ].map((pillar) => (
              <div
                key={pillar.en}
                style={{
                  padding: 'clamp(12px, 2vw, 20px) clamp(8px, 1.5vw, 16px)',
                  background: `${pillar.color}08`,
                  border: `1px solid ${pillar.color}20`,
                  borderTop: `2px solid ${pillar.color}40`,
                  borderRadius: 6,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontFamily: "'Amiri', 'Georgia', serif",
                    fontSize: 'clamp(18px, 3vw, 24px)',
                    color: pillar.color,
                    marginBottom: 6,
                    direction: 'rtl',
                  }}
                >
                  {pillar.ar}
                </div>
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 9,
                    color: `${pillar.color}90`,
                    letterSpacing: '.12em',
                    textTransform: 'uppercase',
                    marginBottom: 4,
                  }}
                >
                  {pillar.en}
                </div>
                <div
                  style={{
                    fontFamily: "'Georgia', serif",
                    fontSize: 10,
                    color: 'rgba(245,240,232,0.35)',
                    lineHeight: 1.4,
                  }}
                >
                  {pillar.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── INTENTIONAL CTA — the crossing is a choice ── */}
        <div
          className={`bridge-reveal ${visible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.72s', marginBottom: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}
        >
          <p
            style={{
              fontFamily: "'Georgia', serif",
              fontSize: 'clamp(12px, 1.6vw, 14px)',
              color: 'rgba(245,240,232,0.35)',
              letterSpacing: '0.04em',
              fontStyle: 'italic',
              marginBottom: 4,
            }}
          >
            The letter is the root. The platform is the fruit.
          </p>
          <Link href="/open-letter" className="bridge-cta-btn">
            <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: 'rgba(201,168,76,0.7)', direction: 'rtl', lineHeight: 1 }}>
              ✦
            </span>
            <span>Read the Foundational Open Letter</span>
            <span style={{ opacity: 0.5 }}>→</span>
          </Link>
          <p
            style={{
              fontFamily: 'monospace',
              fontSize: 8,
              color: 'rgba(201,168,76,0.25)',
              letterSpacing: '.22em',
              textTransform: 'uppercase',
              marginTop: 4,
            }}
          >
            Uboor · عُبُور · The Crossing
          </p>
        </div>

        {/* Scroll progress indicator — subtle bar at bottom */}
        <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)' }}>
          <div style={{ width: 80, height: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 1, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                borderRadius: 1,
                background: 'rgba(201,168,76,0.4)',
                width: `${scrollProgress * 100}%`,
                transition: 'width 0.1s linear',
              }}
            />
          </div>
        </div>
      </div>

      {/* Bottom fade — dissolves into the letter's opening */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '25%',
          background: 'linear-gradient(to bottom, transparent, #060d12)',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />
    </div>
  );
}
